<template>
  <div class="absolute inset-0 flex size-full flex-col overflow-hidden">
    <CheckoutHeader :redirect-route />

    <RevLoadingScreen
      v-if="isLoading"
      class="bg-surface-default-mid fixed inset-0"
      data-qa="loading-screen"
      :text="''"
    />
    <NuxtPage v-else :billing-address :gift-card @update="handleUpdate" />

    <div id="toast-wrapper" class="fixed z-50">
      <TheToast to="#toast-wrapper" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useHead, useRoute, useRouteLocationWithLocale } from '#imports'
import { computed, onMounted, ref } from 'vue'

import { Country } from '@backmarket/http-api/src/standards/Country'
import type { Bill } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import CheckoutHeader from '@backmarket/nuxt-layer-checkout/components/CheckoutHeader/CheckoutHeader.vue'
import {
  FraudDetectionStep,
  useFraudDetectionScripts,
} from '@backmarket/nuxt-layer-payment/fraud/composables/useFraudDetectionScripts'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import TheToast from '@backmarket/nuxt-module-toast/TheToast.vue'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import { CHECKOUT } from '~/scopes/checkout/routes-names'
import { HOME } from '~/scopes/home/route-names'

import translations from './GiftCardFlow.translations'

const i18n = useI18n()
const currentLocale = useI18nLocale()
const route = useRoute()
const $httpFetch = useHttpFetch()
const resolveLocalizedRoute = useRouteLocationWithLocale()

const isLoading = ref(false)
const giftCard = ref<
  | ApiSpecs['/api/gift-cards/v1/product-page/giftcard/request/:requestId']['GET']['response']
  | null
>(null)

async function fetchGiftCard() {
  try {
    return await $httpFetch(
      '/api/gift-cards/v1/product-page/giftcard/request/:requestId',
      {
        pathParams: { requestId: route.params.requestId.toString() },
      },
    )
  } catch (error) {
    return null
  }
}

onMounted(async () => {
  isLoading.value = true
  giftCard.value = await fetchGiftCard()
  isLoading.value = false
})

useHead({
  title: i18n(translations.title),
  htmlAttrs: { lang: currentLocale },
  meta: [
    {
      name: 'description',
      content: i18n(translations.description),
    },
  ],
})

useFraudDetectionScripts({
  currentStep: computed(
    (): FraudDetectionStep =>
      route.name !== CHECKOUT.PAYMENT_RESULT
        ? 'CHECKOUT_IN_PROGRESS'
        : 'CHECKOUT_ENDED',
  ),
})

async function handleUpdate(callback: () => void) {
  isLoading.value = true
  giftCard.value = await fetchGiftCard()
  isLoading.value = false

  callback()
}

const billingAddress = computed(() => {
  return transformBillingAddress(giftCard.value?.billingAddress ?? null)
})

const redirectRoute = computed(() => {
  return resolveLocalizedRoute({ name: HOME })
})

function transformBillingAddress(
  data:
    | ApiSpecs['/api/gift-cards/v1/product-page/giftcard/request/:requestId']['GET']['response']['billingAddress']
    | null,
): Bill | null {
  return {
    birthdate: null,
    city: data?.city || null,
    company: null,
    country: (data?.country as Country) || null,
    countryDialInCode: null,
    customerIdNumber: null,
    dateCreation: null,
    dateModification: null,
    email: null,
    firstName: data?.firstName || null,
    firstNamePronunciation: null,
    lastName: data?.lastName || null,
    lastNamePronunciation: null,
    phone: null,
    postalCode: data?.postalCode || null,
    stateOrProvince: data?.stateOrProvince || null,
    street: data?.street || null,
    street2: data?.street2 || null,
  }
}
</script>
